import { createTheme } from '@mui/material/styles';

// Hex palette for MUI (must be concrete values for color math)
const paletteHex = {
  brand: '#0FA3B1',
  ink: '#0F2A37',
  bg: '#F8FAFC',
  card: '#FFFFFF',
  line: '#E6EEF3',
  slate600: '#475569',
  success: '#16A34A',
  warning: '#F59E0B',
  danger: '#EF4444',
};

// CSS variable references for use in sx/styleOverrides
const brandColors = {
  electricTeal: 'var(--brand)',
  neonLime: 'var(--accent)',
  midnightSteel: 'var(--ink)',
  clearLight: 'var(--bg)',
  line: 'var(--line)'
};

export const theme = createTheme({
  palette: {
    primary: {
      main: paletteHex.brand,
      dark: paletteHex.ink,
      light: paletteHex.card,
      contrastText: '#ffffff'
    },
    secondary: {
      main: paletteHex.slate600,
      contrastText: '#ffffff',
    },
    error: { main: paletteHex.danger },
    warning: { main: paletteHex.warning },
    success: { main: paletteHex.success },
    background: {
      default: paletteHex.bg,
      paper: paletteHex.card,
    },
    text: {
      primary: paletteHex.ink,
      secondary: paletteHex.slate600
    },
    divider: paletteHex.line,
  },
  shape: {
    borderRadius: 16,
  },
  typography: {
    fontFamily: '"Montserrat", "Helvetica", "Arial", sans-serif',
    allVariants: {
      fontFeatureSettings: '"tnum" 1, "ss01" 1',
    },
    h1: {
      fontWeight: 700,
      fontSize: '2rem', // 32px
      lineHeight: 1.2,
      color: brandColors.midnightSteel,
    },
    h2: {
      fontWeight: 600,
      fontSize: '1.5rem', // 24px
      lineHeight: 1.3,
      color: brandColors.midnightSteel,
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.125rem', // 18px
      lineHeight: 1.35,
      color: brandColors.midnightSteel,
    },
    body1: {
      fontWeight: 400,
      fontSize: '1rem', // 16px
      lineHeight: 1.6,
      color: brandColors.midnightSteel,
    },
    body2: {
      fontWeight: 400,
      fontSize: '0.875rem', // 14px
      lineHeight: 1.6,
      color: 'var(--muted)',
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: 'var(--bg)',
          color: 'var(--ink)'
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 'var(--radius)',
          textTransform: 'none',
          fontWeight: 600,
          transition: 'transform 180ms cubic-bezier(0.2,0,0,1), box-shadow 180ms cubic-bezier(0.2,0,0,1), opacity 180ms',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: 'var(--shadow-sm)'
          },
          '&.Mui-focusVisible': {
            boxShadow: '0 0 0 2px var(--accent)'
          },
          paddingInline: 16,
          height: 40,
        },
        contained: {
          backgroundColor: 'var(--brand)',
          color: '#ffffff',
          '&:hover': { opacity: 0.9, backgroundColor: 'var(--brand)' },
        },
        outlined: {
          borderColor: 'var(--line)',
          color: 'var(--ink)',
          backgroundColor: 'transparent',
          '&:hover': { backgroundColor: 'var(--slate-100)' },
        },
        text: {
          color: 'var(--ink)',
          '&:hover': { backgroundColor: 'var(--slate-100)' },
        },
      },
      variants: [
        {
          // tertiary
          props: { variant: 'outlined', color: 'primary' },
          style: {
            backgroundColor: 'var(--slate-50)',
            borderColor: 'var(--line)',
          },
        },
        {
          // destructive
          props: { variant: 'contained', color: 'error' },
          style: {
            backgroundColor: 'var(--danger)',
          },
        },
      ],
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 'var(--radius)',
          boxShadow: 'var(--shadow-sm)',
          border: '1px solid var(--line)',
          transition: 'transform 180ms cubic-bezier(0.2,0,0,1), box-shadow 180ms',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: 'var(--shadow-md)'
          }
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'var(--bg)',
          color: 'var(--ink)',
          boxShadow: 'none',
          borderBottom: '1px solid var(--line)'
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: 'var(--card)'
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          fontWeight: 600,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { height: 6, borderRadius: 6, backgroundColor: 'var(--slate-100)' },
        bar: { borderRadius: 6, backgroundColor: 'var(--brand)' },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          color: 'var(--ink)',
          '&.Mui-selected': {
            backgroundColor: 'var(--slate-100)',
            color: 'var(--ink)'
          },
          '&:hover': {
            backgroundColor: 'var(--slate-100)'
          },
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          color: 'var(--ink)'
        }
      }
    },
  },
});

export { brandColors };
